import AppLoading from "@/components/AppLoading";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import React, { useEffect, useMemo, useState } from "react";
import {
  IvrIntegrationModel,
  PronunciationLexicon,
  IVRVoiceConfiguration
} from "../types";
import SmsIntegrationEditModal from "./SmsIntegrationEditModal";
import SmsIntegrationSetupModal from "./SmsIntegrationSetupModal";
import IvrIntegrationEditModal from "./IvrIntegrationEditModal";
import {
  PrimaryBlueButton,
  SecondaryBlueButton,
  IconPlus,
  Column,
  TableBase,
  Severity
} from "@velaro/velaro-component-lib";
import PronunciationActionBar from "./PronunciationActionBar";
import PronunciationLexiconModal from "./PronunciationLexiconModal";
import IVRVoiceConfigurationModal from "./IVRVoiceConfigurationModal";
import useToastAlert from "@/hooks/useToastAlert";

const pronunciationColumns: Column[] = [
  { label: "Written Word or Phrase", accessor: "misrecognitions" },
  { label: "Spoken Pronunciation", accessor: "correctedText" },
  { label: "Type", accessor: "type" },
  { label: "Team", accessor: "team" },
  { label: "Active", accessor: "isActive" },
  { label: "Actions", accessor: "actions", disableSort: true }
];

const voiceConfigurationColumns: Column[] = [
  { label: "Configuration Name", accessor: "name" },
  { label: "Voice Provider", accessor: "voiceProvider" },
  { label: "Voice ID", accessor: "voiceId" },
  { label: "Team", accessor: "team" },
  { label: "Actions", accessor: "actions", disableSort: true }
];

export default function IvrIntegration() {
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState<IvrIntegrationModel[]>([]);
  const [editedIntegrationId, setEditedIntegrationId] = useState<number>();
  const [editError, setEditError] = useState("");
  const api = useApi();

  // Pronunciation Lexicons state
  const [lexicons, setLexicons] = useState<PronunciationLexicon[]>([]);
  const [showLexiconModal, setShowLexiconModal] = useState(false);
  const [editedLexicon, setEditedLexicon] = useState<
    PronunciationLexicon | undefined
  >(undefined);

  // Voice Configurations state
  const [voiceConfigurations, setVoiceConfigurations] = useState<
    IVRVoiceConfiguration[]
  >([]);
  const [showVoiceConfigModal, setShowVoiceConfigModal] = useState(false);
  const [editedVoiceConfiguration, setEditedVoiceConfiguration] = useState<
    IVRVoiceConfiguration | undefined
  >(undefined);

  const { displayToast } = useToastAlert();

  const fetchIntegrations = React.useCallback(async () => {
    const response = await api.messaging.get("Sms/Integrations");
    const data: IvrIntegrationModel[] = await response.json();
    setIntegrations(data.filter((integration) => integration.isIvr));
    setLoading(false);
  }, [api.messaging]);

  const fetchLexicons = React.useCallback(async () => {
    const response = await api.messaging.get("PronunciationLexicons");
    const data: PronunciationLexicon[] = await response.json();
    setLexicons(data);
  }, [api.messaging]);

  const fetchVoiceConfigurations = React.useCallback(async () => {
    const response = await api.messaging.get("IVRVoiceConfigurations");
    const data: IVRVoiceConfiguration[] = await response.json();
    setVoiceConfigurations(data);
  }, [api.messaging]);

  useEffect(() => {
    fetchIntegrations();
    fetchLexicons();
    fetchVoiceConfigurations();

    // Listen for refresh event from header button
    const handleRefresh = () => {
      fetchIntegrations();
    };

    window.addEventListener("refreshIvrIntegrations", handleRefresh);

    return () => {
      window.removeEventListener("refreshIvrIntegrations", handleRefresh);
    };
  }, [fetchIntegrations, fetchLexicons, fetchVoiceConfigurations]);

  const { getTeamNameById } = useTeams();

  const errorMap: Record<string, string> = {
    release_error: "An error occured while releasing the phone number.",
    update_error: "An error occured while updating the phone number."
  };

  const editedIntegration = useMemo(() => {
    return integrations.find((x) => x.id === editedIntegrationId);
  }, [editedIntegrationId, integrations]);

  const lexiconTableData = useMemo(() => {
    return lexicons.map((lexicon) => ({
      ...lexicon,
      team: lexicon.teamId
        ? getTeamNameById(Number(lexicon.teamId))
        : "All Teams",
      isActive: lexicon.isActive ? "Yes" : "No",
      misrecognitions:
        lexicon.misrecognitions.length > 50
          ? lexicon.misrecognitions.substring(0, 50) + "..."
          : lexicon.misrecognitions,
      actions: (
        <PronunciationActionBar
          onEdit={() => handleEditLexicon(lexicon)}
          onDelete={() => deleteLexicon(lexicon.id)}
        />
      )
    }));
  }, [lexicons, getTeamNameById]);

  const voiceConfigurationTableData = useMemo(() => {
    return voiceConfigurations.map((config) => ({
      ...config,
      team: config.teamId
        ? getTeamNameById(Number(config.teamId))
        : "All Teams",
      actions: (
        <PronunciationActionBar
          onEdit={() => handleEditVoiceConfiguration(config)}
          onDelete={() => deleteVoiceConfiguration(config.id)}
        />
      )
    }));
  }, [voiceConfigurations, getTeamNameById]);

  if (loading) {
    return <AppLoading />;
  }

  function editIntegration(id: number) {
    setEditedIntegrationId(id);
  }

  async function release() {
    setLoading(true);
    if (confirm("Are you sure you want to remove this integration?")) {
      try {
        setIntegrations(
          integrations.filter((i) => i.id !== editedIntegrationId)
        );
        const resp = await api.messaging.delete(
          `Sms/Integrations/${editedIntegrationId}`
        );
        if (resp.ok) {
          setEditedIntegrationId(undefined);
          setEditError("");
        }
      } catch (e) {
        console.log(e);
        setEditError(errorMap["release_error"]);
      } finally {
        setLoading(false);
      }
    }
  }

  async function update(integration: IvrIntegrationModel) {
    setLoading(true);
    try {
      const newIntegrations = [...integrations];
      const index = integrations.findIndex((x) => x.id === editedIntegrationId);
      newIntegrations[index] = integration;
      setIntegrations(newIntegrations);

      const resp = await api.messaging.post(
        "Sms/Integrations/UpdatePhoneNumber",
        {
          Id: editedIntegrationId,
          TeamId: integration.teamId,
          IVRVoiceConfigurationId: integration.ivrVoiceConfigurationId
        }
      );
      if (resp.ok) {
        setEditedIntegrationId(undefined);
        setEditError("");
      } else {
        setEditError(errorMap["update_error"]);
      }
    } catch (e) {
      console.log(e);
      setEditError(errorMap["update_error"]);
    } finally {
      setLoading(false);
    }
  }

  async function deleteLexicon(id: number) {
    if (
      confirm("Are you sure you want to delete this pronunciation correction?")
    ) {
      try {
        const resp = await api.messaging.delete(`PronunciationLexicons/${id}`);
        if (resp.ok) {
          setLexicons(lexicons.filter((x) => x.id !== id));
          displayToast(
            Severity.Success,
            "Pronunciation correction deleted successfully."
          );
        } else {
          displayToast(
            Severity.Error,
            "Failed to delete pronunciation correction."
          );
        }
      } catch (e) {
        console.log(e);
        displayToast(Severity.Error, "An error occurred while deleting.");
      }
    }
  }

  function handleEditLexicon(lexicon: PronunciationLexicon) {
    setEditedLexicon(lexicon);
    setShowLexiconModal(true);
  }

  function handleSaveLexicon(lexicon: PronunciationLexicon) {
    if (editedLexicon) {
      // Update existing
      setLexicons(lexicons.map((x) => (x.id === lexicon.id ? lexicon : x)));
    } else {
      // Add new
      setLexicons([...lexicons, lexicon]);
    }
    setEditedLexicon(undefined);
  }

  async function deleteVoiceConfiguration(id: number) {
    if (confirm("Are you sure you want to delete this voice configuration?")) {
      try {
        const resp = await api.messaging.delete(`IVRVoiceConfigurations/${id}`);
        if (resp.ok) {
          setVoiceConfigurations(
            voiceConfigurations.filter((x) => x.id !== id)
          );
          displayToast(
            Severity.Success,
            "Voice configuration deleted successfully."
          );
        } else {
          displayToast(Severity.Error, "Failed to delete voice configuration.");
        }
      } catch (e) {
        console.log(e);
        displayToast(Severity.Error, "An error occurred while deleting.");
      }
    }
  }

  function handleEditVoiceConfiguration(config: IVRVoiceConfiguration) {
    setEditedVoiceConfiguration(config);
    setShowVoiceConfigModal(true);
  }

  function handleSaveVoiceConfiguration(config: IVRVoiceConfiguration) {
    if (editedVoiceConfiguration) {
      // Update existing
      setVoiceConfigurations(
        voiceConfigurations.map((x) => (x.id === config.id ? config : x))
      );
    } else {
      // Add new
      setVoiceConfigurations([...voiceConfigurations, config]);
    }
    setEditedVoiceConfiguration(undefined);
  }

  return (
    <>
      <div className="my-5">
        <div className="text-lg font-medium text-slate-800 mb-4">
          IVR Phone Numbers
        </div>
        <table className="w-1/2 bg-white">
          <thead>
            <tr>
              <th className="border p-2 text-left">Phone Number</th>
              <th className="border p-2 text-left">Team</th>
              <th className="border p-2 text-left" style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {integrations.map((integration) => (
              <tr key={integration.id}>
                <td className="border p-2">{integration.phoneNumber}</td>
                <td className="border p-2">
                  {getTeamNameById(Number(integration.teamId))}
                </td>
                <td className="border p-2 whitespace-nowrap text-sm">
                  <button
                    className="underline"
                    onClick={() => {
                      editIntegration(integration.id);
                    }}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="my-8">
        <div className="flex justify-between items-center mb-4">
          <div className="text-lg font-medium text-slate-800">
            IVR Pronunciation Corrections
          </div>
          <SecondaryBlueButton
            prefixIcon={<IconPlus />}
            onClick={() => {
              setEditedLexicon(undefined);
              setShowLexiconModal(true);
            }}
            label="Add Correction"
          />
        </div>
        <div className="overflow-auto">
          <TableBase columns={pronunciationColumns} data={lexiconTableData} />
        </div>
      </div>

      <div className="my-8">
        <div className="flex justify-between items-center mb-4">
          <div className="text-lg font-medium text-slate-800">
            IVR Voice Configurations
          </div>
          <SecondaryBlueButton
            prefixIcon={<IconPlus />}
            onClick={() => {
              setEditedVoiceConfiguration(undefined);
              setShowVoiceConfigModal(true);
            }}
            label="Add Configuration"
          />
        </div>
        <div className="overflow-auto">
          <TableBase
            columns={voiceConfigurationColumns}
            data={voiceConfigurationTableData}
          />
        </div>
      </div>

      {editedIntegration && (
        <IvrIntegrationEditModal
          onClose={() => {
            setEditedIntegrationId(undefined);
            setEditError("");
          }}
          release={release}
          update={update}
          integration={editedIntegration}
          error={editError}
        />
      )}
      {showLexiconModal && (
        <PronunciationLexiconModal
          show={showLexiconModal}
          onClose={() => {
            setShowLexiconModal(false);
            setEditedLexicon(undefined);
          }}
          onSave={handleSaveLexicon}
          existingLexicon={editedLexicon}
        />
      )}
      {showVoiceConfigModal && (
        <IVRVoiceConfigurationModal
          show={showVoiceConfigModal}
          onClose={() => {
            setShowVoiceConfigModal(false);
            setEditedVoiceConfiguration(undefined);
          }}
          onSave={handleSaveVoiceConfiguration}
          existingConfiguration={editedVoiceConfiguration}
        />
      )}
    </>
  );
}
